import { WalletIcon } from "@phosphor-icons/react/dist/csr/Wallet";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { Alert, AlertDescription } from "@volca/ui/components/ui/alert";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
} from "@volca/ui/components/ui/empty";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { cn } from "@volca/ui/lib/utils";
import { PayBreakdownPaneShell } from "@/payroll/pay-breakdown/pay-breakdown-pane-shell";
import { PayBreakdownSpreadsheetViewer } from "@/payroll/pay-breakdown/pay-breakdown-spreadsheet-viewer";
import { PAYROLL_GRID_CONTAINER_CLASS_NAME } from "@/payroll/pay-breakdown/payroll-grid-layout";
import {
  PAY_BREAKDOWN_RUN_TARGET_TYPE,
  PAY_BREAKDOWN_SECTION,
} from "@/payroll/pay-breakdown/types";
import { PayBreakdownRunOutput } from "./pay-breakdown-run-output";
import { PayrollRunPage } from "./payroll-run-page";
import type { PayBreakdownPageState } from "./use-pay-breakdown-page";

interface PayBreakdownGridSectionProps {
  page: PayBreakdownPageState;
}

export const PayBreakdownGridSection = ({
  page,
}: PayBreakdownGridSectionProps) => {
  const {
    businessId,
    selectedPeriodStart,
    periodNotFound,
    isLocked,
    isFuturePeriod,
    sheetApprovalActions,
    approvalStatus,
    output,
    hasActiveRun,
    isBeforeBusinessCreated,
    activeSection,
    isCreatingAiRun,
    isTriggeringRun,
    handleEditedCellSelectionChange,
    setIsSheetMaximized,
    setSelectionContext,
    spreadsheetViewerRef,
    handleAskAiAboutCell,
    handleViewEditedCell,
    isSidebarOpen,
    showLegacyDetailLoading,
    runTarget,
    business,
    handleRunPreferredPayroll,
    noteComposer,
  } = page;
  const reveal = page.runReveal;

  return (
    <Stack
      align="start"
      gap={4}
      className={cn("min-h-0 w-full min-w-0 flex-1", {
        hidden: activeSection !== PAY_BREAKDOWN_SECTION.breakdown,
      })}
    >
      {(!selectedPeriodStart || periodNotFound) && (
        <Empty className={emptyStateVariants({ layout: "panel" })}>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <WalletIcon aria-hidden />
            </EmptyMedia>
            <EmptyDescription>
              {periodNotFound
                ? "No payroll details are available for this period."
                : "Select a period to view payroll details."}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}
      {(isBeforeBusinessCreated || showLegacyDetailLoading) && (
        <Stack className="w-full" align="start" gap={4}>
          {isBeforeBusinessCreated && (
            <Alert color="warning" role="status">
              <WarningIcon />
              <AlertDescription>
                <Text variant="caption">
                  This period is before this business was created.
                </Text>
                <Text tone="muted">
                  Existing payroll data is still visible, but new ICM runs can't
                  be requested for this period.
                </Text>
              </AlertDescription>
            </Alert>
          )}
          {showLegacyDetailLoading && (
            <Spinner
              layout="centered"
              className="h-[400px]"
              size="lg"
              tone="primary"
            />
          )}
        </Stack>
      )}

      {reveal.step && (
        <PayrollRunPage
          step={reveal.step}
          canRun={
            !isFuturePeriod &&
            (runTarget.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript
              ? runTarget.canAccessRerun
              : runTarget.type !== PAY_BREAKDOWN_RUN_TARGET_TYPE.none)
          }
          disabledReason={runTarget.disabledReason}
          canEditRules={
            runTarget.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript &&
            business?.can_edit_payroll_rules === true
          }
          onRun={handleRunPreferredPayroll}
        />
      )}

      {output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript && (
        <PayBreakdownPaneShell
          gap={0}
          aria-hidden={reveal.holding || undefined}
          inert={reveal.holding || undefined}
          className={cn(PAYROLL_GRID_CONTAINER_CLASS_NAME, {
            "invisible h-0 min-h-0 flex-none overflow-hidden": reveal.holding,
            "animate-in duration-500 fade-in motion-reduce:animate-none":
              reveal.revealed,
          })}
        >
          <PayBreakdownSpreadsheetViewer
            key={output.runId}
            ref={spreadsheetViewerRef}
            businessId={businessId}
            runId={output.runId}
            fillHeight
            isGlobalSearchEnabled={
              activeSection === PAY_BREAKDOWN_SECTION.breakdown
            }
            onSelectionContextChange={setSelectionContext}
            onAskAiAboutCell={handleAskAiAboutCell}
            onViewEditedCell={handleViewEditedCell}
            onNoteFromSelection={
              noteComposer.canOpen ? noteComposer.open : undefined
            }
            onEditedCellSelectionChange={handleEditedCellSelectionChange}
            sidebarOpen={isSidebarOpen}
            onMaximizedChange={setIsSheetMaximized}
            isLocked={
              isLocked || hasActiveRun || isCreatingAiRun || isTriggeringRun
            }
            approvalStatus={approvalStatus}
            onSheetApprovalChange={sheetApprovalActions.handleChange}
            pendingSheetApproval={sheetApprovalActions.pendingApproval}
          />
        </PayBreakdownPaneShell>
      )}

      {selectedPeriodStart &&
        !periodNotFound &&
        output.type !== PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript && (
          <PayBreakdownRunOutput page={page} />
        )}
    </Stack>
  );
};
